import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Clock, Star, Users } from "lucide-react";
import { api } from "@/lib/api";
import type { Creator, Service, Testimonial } from "@/lib/types";
import { ServiceCard } from "@/components/ServiceCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { relTime } from "@/lib/format";

interface Payload {
  creator: Creator;
  services: Service[];
  testimonials: Testimonial[];
  activeJobsCount: number;
}

export function CreatorPage() {
  const { handle } = useParams<{ handle: string }>();
  const [data, setData] = useState<Payload | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get<Payload>(`/api/creators/${handle}`);
        if (alive) setData(res);
      } catch {
        if (alive) setNotFound(true);
      }
    })();
    return () => { alive = false; };
  }, [handle]);

  if (notFound) {
    return (
      <div className="rounded-lg border border-dashed p-12 text-center text-sm text-muted-foreground">
        No creator found at @{handle}.
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-32 w-full" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const { creator, services, testimonials, activeJobsCount } = data;
  return (
    <div className="space-y-10">
      <section className="rounded-xl border bg-gradient-to-br from-background to-muted/40 p-6 md:p-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={creator.avatar_url ?? undefined} />
              <AvatarFallback className="text-lg">
                {(creator.display_name ?? creator.handle).slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
                {creator.display_name ?? `@${creator.handle}`}
              </h1>
              <p className="text-sm text-muted-foreground">@{creator.handle}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={creator.creator_open ? "default" : "secondary"}>
              {creator.creator_open ? "Open for commissions" : "Currently closed"}
            </Badge>
            {creator.creator_turnaround_days && (
              <Badge variant="outline" className="gap-1">
                <Clock className="h-3 w-3" /> ~{creator.creator_turnaround_days}d turnaround
              </Badge>
            )}
            <Badge variant="outline" className="gap-1">
              <Users className="h-3 w-3" /> {activeJobsCount} active
            </Badge>
          </div>
        </div>
        {creator.bio && (
          <p className="mt-4 max-w-3xl text-sm text-muted-foreground">{creator.bio}</p>
        )}
        {creator.creator_queue_note && (
          <div className="mt-4 rounded-md border border-dashed bg-background/60 p-3 text-xs text-muted-foreground">
            {creator.creator_queue_note}
          </div>
        )}
      </section>

      <section>
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Services</h2>
            <p className="text-sm text-muted-foreground">
              What it costs, what it takes, and how to request it.
            </p>
          </div>
        </div>
        {services.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            This creator hasn't published services yet.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.map(s => (
              <ServiceCard
                key={s.id}
                service={s}
                creatorHandle={creator.handle}
                open={!!creator.creator_open}
              />
            ))}
          </div>
        )}
      </section>

      {testimonials.length > 0 && (
        <section>
          <h2 className="mb-4 text-xl font-semibold tracking-tight">Kudos</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {testimonials.map((t, i) => (
              <div key={i} className="rounded-lg border bg-card p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-1 text-amber-500">
                    {Array.from({ length: t.rating }).map((_, k) => (
                      <Star key={k} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <span className="text-xs text-muted-foreground">{relTime(t.created_at)}</span>
                </div>
                {t.body && <p className="text-sm">{t.body}</p>}
                <p className="mt-2 text-xs text-muted-foreground">
                  — {t.display_name ?? `@${t.handle}`}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
